import { galleryImages as galleryImagePaths } from './imagePaths';

export interface GalleryImage {
  id: string;
  title: string;
  category: string;
  description: string;
  beforeImage: string;
  afterImage: string;
  projectType: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  timeToComplete: string;
  costRange: string;
  tags: string[];
  dateAdded: string;
  featured: boolean;
}

export const galleryImages: GalleryImage[] = [
  {
    id: 'pool-waterproofing',
    title: 'Complete Pool Waterproofing',
    category: 'Submerged',
    description: 'Complete pool waterproofing using Flexcrete. Applied behind tile line to prevent efflorescence, repaired cracks, and sealed around all fittings. Pool now holds water perfectly with no leaks.',
    beforeImage: galleryImagePaths.completePoolWaterproofing,
    afterImage: galleryImagePaths.completePoolWaterproofing,
    projectType: 'Pool Waterproofing',
    difficulty: 'Advanced',
    timeToComplete: '3-5 days',
    costRange: 'Contact for quote',
    tags: ['pool', 'submerged', 'waterproofing', 'efflorescence', 'aquaarmour'],
    dateAdded: '2024-01-20',
    featured: true
  },
  {
    id: 'basement-waterproofing',
    title: 'Basement Moisture Protection',
    category: 'Below Grade',
    description: 'Interior basement waterproofing with Flexcrete. Eliminated moisture intrusion, prevented dampness and stains, repaired foundation cracks, and sealed window frames. Basement now completely dry.',
    beforeImage: galleryImagePaths.basementMoistureProtection,
    afterImage: galleryImagePaths.basementMoistureProtection,
    projectType: 'Basement Waterproofing',
    difficulty: 'Intermediate',
    timeToComplete: '2-3 days',
    costRange: 'Contact for quote',
    tags: ['basement', 'below-grade', 'moisture', 'aquaarmour', 'crack-repair'],
    dateAdded: '2024-01-18',
    featured: true
  },
  {
    id: 'balcony-deck-waterproofing',
    title: 'Balcony & Deck Waterproofing',
    category: 'Above Grade',
    description: 'Critical flashing at wall-balcony cold joint with complete deck waterproofing. Applied Flexcrete to entire balcony including underside. Created proper pitch for drainage. Ready for tile installation.',
    beforeImage: galleryImagePaths.balconyDeckWaterproofing,
    afterImage: galleryImagePaths.balconyDeckWaterproofing,
    projectType: 'Balcony Waterproofing',
    difficulty: 'Intermediate',
    timeToComplete: '2-4 days',
    costRange: 'Contact for quote',
    tags: ['balcony', 'deck', 'flashing', 'cold-joint', 'aquaarmour'],
    dateAdded: '2024-01-16',
    featured: true
  },
  {
    id: 'roof-restoration',
    title: 'Complete Roof Waterproofing',
    category: 'Above Grade',
    description: 'Flat roof waterproofing with Flexcrete. Repaired chimney flashing, sealed between roof levels, and applied UV-resistant waterproof membrane. Roof now completely weather-tight.',
    beforeImage: galleryImagePaths.completeRoofWaterproofing,
    afterImage: galleryImagePaths.completeRoofWaterproofing,
    projectType: 'Roof Waterproofing',
    difficulty: 'Advanced',
    timeToComplete: '3-6 days',
    costRange: 'Contact for quote',
    tags: ['roof', 'flashing', 'uv-resistant', 'aquaarmour', 'chimney'],
    dateAdded: '2024-01-15',
    featured: true
  },
  {
    id: 'shower-pan-waterproofing',
    title: 'Shower Pan Waterproofing',
    category: 'Interior',
    description: 'Complete shower pan waterproofing with Flexcrete. Created impermeable barrier in shower floor and walls. Provides excellent substrate for tile installation. No more water escaping bathroom.',
    beforeImage: galleryImagePaths.showerPanWaterproofing,
    afterImage: galleryImagePaths.showerPanWaterproofing,
    projectType: 'Bathroom Waterproofing',
    difficulty: 'Intermediate',
    timeToComplete: '1-2 days',
    costRange: 'Contact for quote',
    tags: ['shower', 'bathroom', 'interior', 'tile-substrate', 'aquaarmour'],
    dateAdded: '2024-01-14',
    featured: false
  },
  {
    id: 'foundation-protection',
    title: 'Foundation Waterproofing',
    category: 'Below Grade',
    description: 'Critical foundation-to-wall joint waterproofing with Flexcrete. Applied flashing at cold joint to prevent water intrusion. Works on both positive and negative water pressure.',
    beforeImage: galleryImagePaths.foundationWaterproofing,
    afterImage: galleryImagePaths.foundationWaterproofing,
    projectType: 'Foundation Protection',
    difficulty: 'Advanced',
    timeToComplete: '2-4 days',
    costRange: 'Contact for quote',
    tags: ['foundation', 'below-grade', 'flashing', 'aquaarmour', 'cold-joint'],
    dateAdded: '2024-01-12',
    featured: true
  },
  {
    id: 'spa-waterproofing',
    title: 'Spa Waterproofing & Repair',
    category: 'Submerged',
    description: 'Complete spa waterproofing using Flexcrete. Repaired leaks around fittings, waterproofed behind tile line to prevent efflorescence, and sealed all cracks. Spa now holds water perfectly.',
    beforeImage: galleryImagePaths.spaWaterproofingRepair,
    afterImage: galleryImagePaths.spaWaterproofingRepair,
    projectType: 'Spa Waterproofing',
    difficulty: 'Advanced',
    timeToComplete: '2-3 days',
    costRange: 'Contact for quote',
    tags: ['spa', 'submerged', 'waterproofing', 'aquaarmour', 'efflorescence'],
    dateAdded: '2024-01-10',
    featured: false
  },
  {
    id: 'facade-waterproofing',
    title: 'Façade Waterproofing',
    category: 'Above Grade',
    description: 'Exterior façade waterproofing with Flexcrete. Sprayed protective membrane to prevent moisture intrusion, increase stucco adherence, and eliminate efflorescence deposits.',
    beforeImage: galleryImagePaths.facadeWaterproofing,
    afterImage: galleryImagePaths.facadeWaterproofing,
    projectType: 'Façade Protection',
    difficulty: 'Intermediate',
    timeToComplete: '3-5 days',
    costRange: 'Contact for quote',
    tags: ['facade', 'exterior', 'stucco', 'aquaarmour', 'efflorescence'],
    dateAdded: '2024-01-08',
    featured: false
  },
  {
    id: 'retaining-wall',
    title: 'Retaining Wall Waterproofing',
    category: 'Above Grade',
    description: 'Retaining wall waterproofing with Flexcrete. Prevented water intrusion from behind, eliminated efflorescence deposits, and protected wall covering. Wall now completely moisture-proof.',
    beforeImage: galleryImagePaths.retainingWallWaterproofing,
    afterImage: galleryImagePaths.retainingWallWaterproofing,
    projectType: 'Retaining Wall Protection',
    difficulty: 'Intermediate',
    timeToComplete: '2-3 days',
    costRange: 'Contact for quote',
    tags: ['retaining-wall', 'waterproofing', 'efflorescence', 'aquaarmour'],
    dateAdded: '2024-01-06',
    featured: false
  },
  {
    id: 'fountain-waterproofing',
    title: 'Fountain Waterproofing',
    category: 'Submerged',
    description: 'Decorative fountain waterproofing with Flexcrete. Waterproofed inside of basin and spillover areas. Keeps water contained and prevents unsightly efflorescence stains.',
    beforeImage: galleryImagePaths.fountainWaterproofing,
    afterImage: galleryImagePaths.fountainWaterproofing,
    projectType: 'Water Feature Waterproofing',
    difficulty: 'Intermediate',
    timeToComplete: '1-2 days',
    costRange: 'Contact for quote',
    tags: ['fountain', 'water-feature', 'decorative', 'aquaarmour', 'submerged'],
    dateAdded: '2024-01-04',
    featured: false
  },
  {
    id: 'planter-waterproofing',
    title: 'Planter Waterproofing',
    category: 'Above Grade',
    description: 'Waterproofed interior of concrete planters with Flexcrete. Keeps water inside, prevents staining of adjacent surfaces, and eliminates efflorescence deposits on planter exteriors.',
    beforeImage: galleryImagePaths.planterWaterproofing,
    afterImage: galleryImagePaths.planterWaterproofing,
    projectType: 'Planter Protection',
    difficulty: 'Beginner',
    timeToComplete: '1 day',
    costRange: 'Contact for quote',
    tags: ['planter', 'decorative', 'aquaarmour', 'efflorescence'],
    dateAdded: '2024-01-02',
    featured: false
  },
  {
    id: 'cistern-lining',
    title: 'Cistern Waterproof Lining',
    category: 'Below Grade',
    description: 'Interior waterproof lining for concrete water storage tank using Flexcrete. Resistant to RO water corrosion. Tank now holds water perfectly for potable water storage.',
    beforeImage: galleryImagePaths.cisternWaterproofing,
    afterImage: galleryImagePaths.cisternWaterproofing,
    projectType: 'Storage Tank Lining',
    difficulty: 'Advanced',
    timeToComplete: '3-4 days',
    costRange: 'Contact for quote',
    tags: ['cistern', 'storage-tank', 'potable-water', 'aquaarmour', 'ro-water'],
    dateAdded: '2023-12-28',
    featured: true
  }
];

export const galleryCategories = [
  'All',
  'Below Grade',
  'Above Grade',
  'Submerged',
  'Interior',
  'Roofing',
  'Foundation'
];

export const projectTypes = [
  'All',
  'Pool Waterproofing',
  'Basement Waterproofing',
  'Balcony Waterproofing',
  'Roof Waterproofing',
  'Bathroom Waterproofing',
  'Foundation Protection',
  'Spa Waterproofing',
  'Façade Protection',
  'Retaining Wall Protection',
  'Water Feature Waterproofing',
  'Planter Protection',
  'Storage Tank Lining'
];
